// Guest page. Opens from an asset label: getsafetag.com/?aid=01-ER2-MCC-1A.
// Words: GUEST_PAGE_WORDS.md, approved 2026-10-06. Ported as designed from the live
// index.html guest section: same cap, cards, rows, labels, colors and order.
// No sign-in. The rows come from the loadGuestAsset server step; the database
// stays closed to anyone not signed in. Every write goes through server code:
// Report a Problem (piece 4b), Near Miss and Contractor Access (piece 6), and the scan count.

import { keepAdminBack } from "./admin-way-back";
import "./guest.css";
import "./field-access.css";
import { callServer } from "./firebase";
import { idSegments, type GuestResult, type GuestView, type NocState } from "./guest-asset";
import { EMPTY_SHEET, guestReportProblem, guestReportSheetHtml, type GuestSheet } from "./guest-report";
import { EMPTY_NEAR_MISS, nearMissProblem, nearMissReady, nearMissSheetHtml, type NearMissSheet } from "./near-miss";
import { contractorController } from "./contractor-page";
import { startQrScanner, type QrScanner } from "./qr-scanner";
import { scanAgainTarget } from "./scan-again";

/** Company name on the cap and welcome card. Every label hung today is Alltech's. */
const COMPANY_NAME = "Alltech";

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const SCAN_ICON = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <rect x="3" y="3" width="5" height="5"/><rect x="16" y="3" width="5" height="5"/>
  <rect x="3" y="16" width="5" height="5"/><line x1="21" y1="16" x2="21" y2="21"/>
  <line x1="16" y1="21" x2="21" y2="21"/><line x1="16" y1="16" x2="16" y2="16"/></svg>`;

function cap(siteName: string): string {
  return `<div class="g-cap" id="guestHeader">
    <div style="display:flex; flex-direction:row; align-items:center; justify-content:space-between; width:100%;">
      <div style="display:flex; flex-direction:column; flex:1; min-width:0;">
        <div style="font-size:15px; font-weight:800; color:#111; letter-spacing:-0.3px;">${COMPANY_NAME} <span style="font-weight:700;">${siteName ? `— ${esc(siteName)}` : ""}</span></div>
        <div style="font-size:9px; font-weight:700; color:rgba(0,0,0,0.55); text-transform:uppercase; letter-spacing:0.8px; margin-top:1px;">ELECTRICAL SAFETY PROGRAM</div>
      </div>
      <button class="g-scan-btn-dark" data-g="scan" style="flex-shrink:0;">${SCAN_ICON}Scan Again</button>
    </div>
  </div>`;
}

function badge(v: "YES" | "NO" | null): string {
  if (v === "YES") return `<span class="g-shock-badge g-shock-yes">YES</span>`;
  if (v === "NO") return `<span class="g-shock-badge g-shock-no">NO</span>`;
  return `<span class="g-shock-badge g-shock-unknown">—</span>`;
}

/**
 * NOC node words, verbatim from the live page (populateGuestStripV3, nocNodeData),
 * except the Impending Failure explanation: the live line read "No failure signals
 * observed" under every state, even a red one. Neutral wording, Robert 2026-10-06.
 */
export const NOC_NODES = [
  { label: "Properly Installed", yes: "Satisfactory", no: "Unsatisfactory", sub: "Installed per listing, labeling, and manufacturer instructions — mounting, wiring methods, enclosure type, and clearances. NFPA 70E §130.2(B)(1)" },
  { label: "Properly Maintained", yes: "Satisfactory", no: "Unsatisfactory", sub: "Condition of maintenance assessed. Reflects maintenance history and program compliance. NFPA 70E §130.2(B)(1)" },
  { label: "Within AIC Rating", yes: "Satisfactory", no: "Unsatisfactory — overduty condition. AIC rating exceeded. Upstream isolation required.", sub: "Equipment is rated for the available fault current at this location. An overduty condition increases arc flash risk. NFPA 70E §130.2(B)(1)" },
  { label: "Approved Use", yes: "Satisfactory", no: "Unsatisfactory", sub: "Equipment is being used within the parameters defined by its listing and manufacturer instructions. NFPA 70E §130.2(B)(1)" },
  { label: "Covers Secured", yes: "Satisfactory", no: "Unsatisfactory — conceivable contact risk", sub: "Covers, fasteners, knockouts, and doors are closed and held in place. A missing fastener, unlatched door, or open knockout creates passive exposure risk. NFPA 70E §130.2(B)(1)" },
  { label: "Enclosure Intact", yes: "Satisfactory", no: "Unsatisfactory — credible contact risk", sub: "The enclosure is structurally sound upon normal interaction. A cover that pulls away on touch, door that swings free, or absent cover plate creates active exposure risk. NFPA 70E §130.2(B)(1)" },
  { label: "Impending Failure", yes: "None observed", no: "Failure signals present — escalate before interaction", sub: "Signs of an impending failure: burn marks, arc damage, unusual heat, or abnormal sounds. NFPA 70E §130.2(B)(1)" },
] as const;

/** One NOC dot, styled exactly as the live gNocRender does. */
function nocDot(state: NocState, index: number, active: boolean): string {
  let bg: string;
  let color: string;
  let letter: string;
  if (state === null) {
    bg = active ? "#888" : "#e0e0e0";
    color = active ? "#fff" : "#888";
    letter = "?";
  } else if (state) {
    bg = "#22a566";
    color = "#fff";
    // Impending Failure is the one negatively phrased row: N is the good answer there.
    letter = index === 6 ? "N" : "Y";
  } else {
    bg = "#c62828";
    color = "#fff";
    letter = index === 6 ? "Y" : "N";
  }
  return `<div class="g-noc-dot" style="width:20px;height:20px;min-width:20px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:${color};transition:all 0.2s;transform:scale(${active ? 1.1 : 1});">${letter}</div>`;
}

function nocNode(v: GuestView, i: number, active: number): string {
  const borders = i === 6 ? "grid-column:1/-1;border-right:none;" : `${i % 2 === 0 ? "border-right:1px solid #e8e8e8;" : ""}border-bottom:1px solid #e8e8e8;`;
  return `<div class="g-noc-node" data-noc="${i}" style="display:flex;align-items:center;gap:8px;padding:9px 12px;cursor:pointer;${borders}">
    ${nocDot(v.noc[i] ?? null, i, active === i)}
    <span style="font-size:11px;color:#111;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${NOC_NODES[i]!.label}</span>
  </div>`;
}

function nocDetail(v: GuestView, active: number): string {
  if (active < 0) return `<div id="g-noc-detail" style="display:none;"></div>`;
  const d = NOC_NODES[active]!;
  const s = v.noc[active] ?? null;
  const edge = s === null ? "#bbb" : s ? "#2e7d32" : "#c62828";
  const status = s === null ? "Not yet assessed" : s ? d.yes : d.no;
  const tone = s === null ? "#999" : s ? "#2e7d32" : "#c62828";
  return `<div id="g-noc-detail" style="display:block;margin:0 12px 10px;background:#f8f8f8;border-radius:8px;padding:10px 12px;border-left:3px solid ${edge};">
    <div style="font-size:11px;font-weight:700;color:#111;margin-bottom:3px;line-height:1.4;">${d.label}</div>
    <div style="font-size:10px;font-weight:600;margin-bottom:2px;color:${tone};">${esc(status)}</div>
    <div style="font-size:10px;color:#666;line-height:1.4;">${esc(d.sub)}</div>
  </div>`;
}

function nocCard(v: GuestView, active: number): string {
  return `<div class="g-card" id="g-noc-card">
    <div class="g-card-hdr">Normal Operating Condition Profile</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;border-top:1px solid #e8e8e8;">
      ${[0, 1, 2, 3, 4, 5, 6].map((i) => nocNode(v, i, active)).join("")}
    </div>
    <div class="g-dates-row">
      <div class="g-dates-cell br"><div class="g-data-lbl">Last Assessed</div><div class="g-dates-val">${esc(v.lastAssessed)}</div></div>
      <div class="g-dates-cell br"><div class="g-data-lbl">Last PM</div><div class="g-dates-val">${esc(v.lastPm)}</div></div>
      <div class="g-dates-cell"><div class="g-data-lbl">Last Program Audit</div><div class="g-dates-val">${esc(v.lastAudit)}</div></div>
    </div>
    ${nocDetail(v, active)}
  </div>`;
}

const FIELD_ICON = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`;
const BOLT_ICON = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#CC0000"><polygon points="13,2 3,14 11,14 9,22 21,10 13,10"/></svg>`;
const NEAR_MISS_ICON = `<svg width="14" height="14" viewBox="0 0 100 100">
  <circle cx="32" cy="15" r="10" fill="#111"/>
  <path d="M42 28 L66 4" stroke="#111" stroke-width="9" stroke-linecap="round"/>
  <path d="M42 28 L12 48" stroke="#111" stroke-width="9" stroke-linecap="round"/>
  <path d="M42 28 L54 56" stroke="#111" stroke-width="10" stroke-linecap="round"/>
  <path d="M54 56 L90 60" stroke="#111" stroke-width="9" stroke-linecap="round"/>
  <path d="M54 56 L62 88" stroke="#111" stroke-width="9" stroke-linecap="round"/>
  <path d="M78 78 L96 74 M74 84 L94 82 M70 90 L90 89" stroke="#111" stroke-width="3" stroke-linecap="round"/>
  <rect x="4" y="94" width="70" height="4" rx="2" fill="#111"/></svg>`;

function footerButtons(): string {
  return `<button class="g-v3-esp-btn" data-g="field">${FIELD_ICON}QEW Field Access</button>
    <div class="g-btn-row">
      <button class="g-report-btn" data-g="report"><span class="g-bolt">${BOLT_ICON}</span><span class="g-report-btn-text">Report a Problem</span></button>
      <button class="g-report-btn" data-g="nearmiss"><span class="g-bolt">${NEAR_MISS_ICON}</span><span class="g-report-btn-text">Near Miss</span></button>
      <button class="g-report-btn" data-g="contractor"><span class="g-report-btn-text">Contractor Access</span></button>
    </div>
    <div class="g-brand-mark">Powered by GetESPTag — Connecting People, Equipment, and Safe Work Practices</div>`;
}

/** The full guest page for one asset. explainerOpen and nocActive are the two tap states. */
export function guestPageHtml(v: GuestView, explainerOpen = false, nocActive = -1): string {
  const segs = idSegments(v.assetId)
    .map((s) => `<div class="g-id-seg"><div class="g-id-seg-val">${esc(s.value)}</div><div class="g-id-seg-lbl">${s.label}</div></div>`)
    .join("");
  return `<div id="guestContent"><div class="g-page-body">
    ${cap(v.siteName)}
    <div class="g-welcome">
      <div class="g-welcome-title">Welcome to ${COMPANY_NAME}'s Electrical Safety Program</div>
      <div class="g-welcome-body">This is your gateway to electrical safe work practices and procedures for this equipment.</div>
    </div>
    <div class="g-id-explainer${explainerOpen ? " open" : ""}" id="g-id-explainer">
      <button class="g-id-explainer-toggle" data-g="explainer">
        <div class="g-id-explainer-toggle-left">
          <span class="g-id-explainer-label">Understanding The Asset ID</span>
          <span class="g-id-explainer-badge">${esc(v.assetId)}</span>
        </div>
        <span class="g-id-explainer-chevron">▼</span>
      </button>
      <div class="g-id-explainer-body"><div class="g-id-segments">${segs}</div></div>
    </div>
    <div class="g-card">
      <div class="g-card-hdr">Electrical Equipment Profile</div>
      <div class="g-card-grid2">
        <div class="g-card-cell br"><div class="g-card-lbl">Asset ID</div><div class="g-card-val mono">${esc(v.assetId)}</div></div>
        <div class="g-card-cell"><div class="g-card-lbl">Equipment Type</div><div class="g-card-val">${esc(v.equipmentType)}</div></div>
        <div class="g-card-cell br bt"><div class="g-card-lbl">Device Type</div><div class="g-card-val">${esc(v.deviceType)}</div></div>
        <div class="g-card-cell bt"><div class="g-card-lbl">Room / Area</div><div class="g-card-val">${esc(v.location)}</div></div>
      </div>
      <div class="g-card-full">
        <div class="g-card-lbl">Description</div>
        <div class="g-card-val" style="line-height:1.5;">${esc(v.description)}</div>
      </div>
    </div>
    <div class="g-card">
      <div class="g-card-hdr">Shock &amp; Arc Flash Hazards Profile</div>
      <div class="g-card-grid3">
        <div class="g-card-cell br" style="text-align:center;"><div class="g-card-lbl">Nominal Voltage</div><div class="g-card-val">${esc(v.voltage)}</div></div>
        <div class="g-card-cell br" style="text-align:center;"><div class="g-card-lbl">Limited Approach Boundary</div><div class="g-card-val">${esc(v.limitedApproach)}</div></div>
        <div class="g-card-cell" style="text-align:center;"><div class="g-card-lbl">Shock Hazard</div><div style="margin-top:2px;">${badge(v.shockHazard)}</div></div>
      </div>
      <div class="g-card-grid3">
        <div class="g-card-cell br bt" style="text-align:center;"><div class="g-card-lbl">Incident Energy</div><div class="g-card-val amber">${esc(v.incidentEnergy)}</div></div>
        <div class="g-card-cell br bt" style="text-align:center;"><div class="g-card-lbl">Arc Flash Boundary</div><div class="g-card-val">${esc(v.arcFlashBoundary)}</div></div>
        <div class="g-card-cell bt" style="text-align:center;"><div class="g-card-lbl">Arc Flash Hazard</div><div style="margin-top:2px;">${badge(v.arcFlashHazard)}</div></div>
      </div>
      <div class="g-dates-row">
        <div class="g-dates-cell br"><div class="g-data-lbl">AF Study</div><div class="g-dates-val">${esc(v.afStudy)}</div></div>
        <div class="g-dates-cell"><div class="g-data-lbl">Next Assessment</div><div class="g-dates-val">${esc(v.nextAssessment)}</div></div>
      </div>
    </div>
    ${nocCard(v, nocActive)}
    ${footerButtons()}
  </div></div>`;
}

/** Site not active (00 Nicholasville until cutover). Cap with the site name, then the notice. */
export function notActiveHtml(assetId: string, siteName: string): string {
  return `<div id="guestContent"><div class="g-page-body">
    ${cap(siteName)}
    <div class="g-status">
      <div class="g-status-icon">⏳</div>
      <div class="g-status-title">Site Not Active</div>
      <div class="g-status-body">${esc(siteName || "This site")} is not live in SafeTag yet. Asset <strong>${esc(assetId)}</strong> will show here when the site goes live.</div>
      <div class="g-status-foot">If you believe this is an error, contact your ESP Manager.</div>
    </div>
  </div></div>`;
}

/** Label not in the system. Same words as the live showGuestError. */
export function notFoundHtml(assetId: string): string {
  return `<div id="guestContent"><div class="g-page-body">
    ${cap("")}
    <div class="g-status">
      <div class="g-status-icon">⚠️</div>
      <div class="g-status-title">Asset Not Available</div>
      <div class="g-status-body">Asset <strong>${esc(assetId)}</strong> has not yet been added to the registry, or this site is still being configured.</div>
      <div class="g-status-foot">If you believe this is an error, contact your ESP Manager.</div>
    </div>
  </div></div>`;
}

function loadingHtml(): string {
  return `<div id="guestContent"><div class="g-page-body">${cap("")}<div class="g-loading">Loading asset…</div></div></div>`;
}

function errorHtml(message: string): string {
  return `<div id="guestContent"><div class="g-page-body">${cap("")}
    <div class="g-status">
      <div class="g-status-icon">⚠️</div>
      <div class="g-status-title">Could Not Load This Asset</div>
      <div class="g-status-body">${esc(message)}</div>
      <div class="g-status-foot"><button class="g-report-btn" data-g="retry" style="margin:0 auto;max-width:160px;">Try Again</button></div>
    </div>
  </div></div>`;
}

/** Notices for the buttons whose next page is not on the new app yet. */
export const NOTICES: Record<string, { title: string; body: string }> = {};

/** How long "Found ... ✓" shows before the next label opens. Same as the bag tag scan. */
const SCAN_HOLD_MS = 1000;

/** Scan Again camera, in the Field Access look Robert approved for it (2026-10-13). */
function scanAgainHtml(siteName: string, current: string): string {
  return `<div class="fa-wrap">
    <div class="fa-top"><div><div class="nm">${COMPANY_NAME}${siteName ? ` — ${esc(siteName)}` : ""}</div><div class="pg">Electrical Safety Program</div></div></div>
    <div class="fa-body">
      <div class="fa-card"><h3>Scan Again</h3>
        <div class="fa-cam"><video id="sa-video" playsinline muted></video><div class="fr"></div><div class="tx" id="sa-cam-tx">Starting the camera…</div></div>
        <div class="fa-hint" id="sa-hint">Scan the QR on the next SafeTag label.</div></div>
      <button class="fa-plain" type="button" data-g="scan-stop">Cancel</button>
      ${current ? `<div class="fa-hint" style="text-align:left;padding:0 4px">Viewing ${esc(current)}.</div>` : ""}
    </div>
  </div>`;
}

function noticeHtml(key: string): string {
  const n = NOTICES[key];
  if (!n) return "";
  return `<div class="g-overlay show" data-g="close"></div>
    <div class="g-bottom-sheet open" role="dialog" aria-label="${n.title}">
      <div class="g-sheet-handle"></div>
      <div class="g-sheet-header"><span class="g-sheet-title">${n.title}</span><button class="g-sheet-close" data-g="close">✕</button></div>
      <div class="g-notice-body">${n.body}</div>
    </div>`;
}

/** A random ID for this browser, kept so the server can hold one report per device every 5 minutes. */
function deviceId(): string {
  const make = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
  try {
    const had = localStorage.getItem("safetag.device");
    if (had) return had;
    const id = make();
    localStorage.setItem("safetag.device", id);
    return id;
  } catch {
    return make();
  }
}

/** onFieldAccess opens QEW Field Access for this asset (STEP3_SIGNIN_APPROVED.md). */
export function mountGuestPage(root: HTMLElement, assetId: string, company: string, onFieldAccess?: (siteName: string) => void): void {
  document.body.classList.add("guest");
  let result: GuestResult | null = null;
  let explainerOpen = false;
  let nocActive = -1;
  let notice = "";
  // Piece 4b: Report a Problem. Null when the sheet is shut.
  let report: GuestSheet | null = null;
  // Piece 6: Near Miss, a sheet shaped like Report a Problem. Null when shut.
  let nearMiss: NearMissSheet | null = null;
  // Piece 6: Contractor Access takes the whole page until Back To The Label.
  const contractor = contractorController(assetId, company, deviceId, () => draw(), () => {
    if (sheetStep) {
      sheetStep = false;
      history.back();
    }
    draw();
    window.scrollTo(0, 0);
  });
  let failed = "";
  // Scan Again: the camera takes the page until a label is read or Cancel (2026-10-13).
  let scanning = false;
  let scanner: QrScanner | null = null;
  // An open sheet is a step, so the phone's Back gesture closes it like other apps (approved 2026-10-09).
  let sheetStep = false;
  window.addEventListener("popstate", () => {
    if (!document.body.classList.contains("guest") || !sheetStep) return;
    sheetStep = false;
    stopScan();
    notice = "";
    report = null;
    nearMiss = null;
    if (contractor.isOpen()) contractor.close();
    else draw();
  });

  function draw(): void {
    const siteName = result?.kind === "asset" ? result.view.siteName : result?.kind === "notActive" ? result.siteName : "";
    if (scanning) {
      root.innerHTML = scanAgainHtml(siteName, result?.kind === "asset" ? result.view.assetId : assetId);
      window.scrollTo(0, 0);
      startScan();
      return;
    }
    if (contractor.isOpen()) {
      root.innerHTML = contractor.html(siteName, result?.kind === "asset" ? result.view : null);
      return;
    }
    let html: string;
    if (!result) html = errorHtml(failed);
    else if (result.kind === "asset") html = guestPageHtml(result.view, explainerOpen, nocActive);
    else if (result.kind === "notActive") html = notActiveHtml(result.assetId, result.siteName);
    else html = notFoundHtml(result.assetId);
    const assetLine = result?.kind === "asset" ? [result.view.assetId, result.view.equipmentType, result.view.location].filter(Boolean).join(" · ") : assetId;
    root.innerHTML = html + (report ? guestReportSheetHtml(report, assetLine) : nearMiss ? nearMissSheetHtml(nearMiss, assetLine) : noticeHtml(notice));
  }

  async function load(): Promise<void> {
    root.innerHTML = loadingHtml();
    failed = "";
    try {
      // The device ID lets the server count one scan per phone per 10 minutes (piece 6).
      result = await callServer<GuestResult>("loadGuestAsset", { assetId, company, deviceId: deviceId() });
      if (result.kind === "asset") document.title = `${result.view.assetId} · SafeTag`;
      draw();
    } catch (e) {
      failed = e instanceof Error ? e.message : String(e);
      draw();
    }
  }

  async function sendReport(): Promise<void> {
    if (!report || report.busy) return;
    const problem = guestReportProblem(report);
    if (problem) {
      report = { ...report, error: problem };
      draw();
      return;
    }
    report = { ...report, busy: true, error: "" };
    draw();
    try {
      const res = await callServer<{ requestId: string }>("reportProblem", {
        assetId,
        company,
        picks: report.picks,
        note: report.note,
        name: report.name,
        deviceId: deviceId(),
      });
      if (report) report = { ...report, busy: false, doneId: res.requestId };
    } catch (e) {
      if (report) report = { ...report, busy: false, error: e instanceof Error ? e.message : String(e) };
    }
    draw();
  }

  async function sendNearMiss(): Promise<void> {
    if (!nearMiss || nearMiss.busy) return;
    const problem = nearMissProblem(nearMiss);
    if (problem) {
      nearMiss = { ...nearMiss, error: problem };
      draw();
      return;
    }
    nearMiss = { ...nearMiss, busy: true, error: "" };
    draw();
    try {
      const res = await callServer<{ observationId: string }>("reportNearMiss", {
        assetId,
        company,
        picks: nearMiss.picks,
        note: nearMiss.note,
        name: nearMiss.name,
        deviceId: deviceId(),
      });
      if (nearMiss) nearMiss = { ...nearMiss, busy: false, doneId: res.observationId };
    } catch (e) {
      if (nearMiss) nearMiss = { ...nearMiss, busy: false, error: e instanceof Error ? e.message : String(e) };
    }
    draw();
  }

  function startScan(): void {
    const video = root.querySelector<HTMLVideoElement>("#sa-video");
    const tx = root.querySelector<HTMLElement>("#sa-cam-tx");
    const hint = root.querySelector<HTMLElement>("#sa-hint");
    if (!video || !tx || !hint) return;
    let found = "";
    scanner?.stop();
    scanner = startQrScanner(
      video,
      (link) => {
        if (found) return false;
        const target = scanAgainTarget(link, location.host);
        if (target.kind === "refused") {
          hint.textContent = target.message;
          return false;
        }
        // Hold a moment so the reader sees what was read, like the bag tag scan.
        found = target.id;
        root.querySelector(".fa-cam")?.classList.add("found");
        tx.textContent = `Found ${target.id} ✓`;
        hint.textContent = "Label read. Opening it.";
        window.setTimeout(() => {
          if (scanning) location.assign(new URL(keepAdminBack(target.path, location.search), location.origin).href);
        }, SCAN_HOLD_MS);
        return true;
      },
      (text) => {
        tx.textContent = text.startsWith("Looking") || text.startsWith("Starting") ? text : `${text} Use the phone camera instead.`;
      },
    );
  }

  function stopScan(): void {
    scanner?.stop();
    scanner = null;
    scanning = false;
  }

  function openStep(): void {
    if (!sheetStep) {
      sheetStep = true;
      history.pushState({ guestSheet: true }, "");
    }
  }

  // Typing keeps its place: the note and name are read as they change, without a redraw.
  root.addEventListener("input", (ev) => {
    if (contractor.input(ev)) return;
    const t = ev.target as HTMLInputElement | HTMLTextAreaElement;
    if (nearMiss) {
      if (t.id === "nm-note") nearMiss.note = t.value;
      else if (t.id === "nm-name") nearMiss.name = t.value;
      else return;
      const btn = root.querySelector<HTMLButtonElement>('[data-g="send-nearmiss"]');
      if (btn) btn.disabled = !nearMissReady(nearMiss);
      return;
    }
    if (!report) return;
    if (t.id === "gr-note") report.note = t.value;
    else if (t.id === "gr-name") report.name = t.value;
    else return;
    const btn = root.querySelector<HTMLButtonElement>('[data-g="send-report"]');
    if (btn) btn.disabled = report.busy || report.picks.length === 0;
  });

  root.addEventListener("click", (ev) => {
    if (contractor.isOpen()) {
      contractor.click(ev);
      return;
    }
    const nmPick = (ev.target as HTMLElement).closest<HTMLElement>("[data-nmpick]");
    if (nmPick && nearMiss) {
      const p = nmPick.dataset.nmpick ?? "";
      nearMiss = { ...nearMiss, error: "", picks: nearMiss.picks.includes(p) ? nearMiss.picks.filter((x) => x !== p) : [...nearMiss.picks, p] };
      draw();
      return;
    }
    const pick = (ev.target as HTMLElement).closest<HTMLElement>("[data-gpick]");
    if (pick && report) {
      const p = pick.dataset.gpick ?? "";
      report = { ...report, error: "", picks: report.picks.includes(p) ? report.picks.filter((x) => x !== p) : [...report.picks, p] };
      draw();
      return;
    }
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-g], [data-noc]");
    if (!el) return;
    if (el.dataset.noc !== undefined) {
      const i = Number(el.dataset.noc);
      nocActive = nocActive === i ? -1 : i;
      draw();
      return;
    }
    const g = el.dataset.g ?? "";
    if (g === "explainer") explainerOpen = !explainerOpen;
    else if (g === "send-report") {
      void sendReport();
      return;
    } else if (g === "send-nearmiss") {
      void sendNearMiss();
      return;
    } else if (g === "report") {
      report = { ...EMPTY_SHEET, picks: [] };
      openStep();
    } else if (g === "nearmiss") {
      nearMiss = { ...EMPTY_NEAR_MISS, picks: [] };
      openStep();
    } else if (g === "contractor") {
      // The whole page becomes the contractor's steps. Back To The Label (or the phone's Back) returns here.
      openStep();
      contractor.open();
    } else if (g === "scan") {
      scanning = true;
      openStep();
    } else if (g === "scan-stop") {
      stopScan();
      if (sheetStep) {
        sheetStep = false;
        history.back();
      }
    } else if (g === "close") {
      if (report?.busy || nearMiss?.busy) return;
      notice = "";
      report = null;
      nearMiss = null;
      if (sheetStep) {
        sheetStep = false;
        history.back();
      }
    }
    else if (g === "field" && onFieldAccess) {
      onFieldAccess(result?.kind === "asset" ? result.view.siteName : "");
      return;
    } else if (g === "retry") {
      void load();
      return;
    } else if (g in NOTICES) {
      notice = g;
      if (!sheetStep) {
        sheetStep = true;
        history.pushState({ guestSheet: true }, "");
      }
    }
    else return;
    draw();
  });

  void load();
}
